import React, { useState } from 'react';
import styles from './eterna.module.css';
import maxillaire from "../../assets/img/upper scan.png"
import mandibule from "../../assets/img/lower scan.png"
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase/firebase';

const EternaContent = ({ onBack, patient }) => {
    const [files, setFiles] = useState({
        maxillaire: null,
        mandibule: null
    });
    const [uploading, setUploading] = useState({
        maxillaire: false,
        mandibule: false
    });
    const [uploaded, setUploaded] = useState({
        maxillaire: false,
        mandibule: false
    });
    const [saving, setSaving] = useState(false);

    // Check if any file is currently uploading
    const isAnyUploading = uploading.maxillaire || uploading.mandibule;

    const handleFileSelect = (e, type) => {
        const file = e.target.files[0];
        if (file) {
            /*console.log(`File selected for ${type}:`, file.name, file.size, file.type);*/
            
            // Validate file extension for scan files
            const fileExtension = file.name.split('.').pop().toLowerCase();
            const allowedExtensions = ['stl', 'ply', 'obj'];
            
            if (!allowedExtensions.includes(fileExtension)) {
                alert("Veuillez sélectionner un fichier STL, PLY ou OBJ valide");
                e.target.value = '';
                return;
            }
            
            /*console.log(`Starting upload process for ${type}...`);*/
            
            setFiles(prev => ({
                ...prev,
                [type]: file
            }));
            
            // Start upload immediately
            uploadFile(file, type);
        }
    };

    const uploadFile = async (file, type) => {
        /*console.log(`Upload function called for ${type}`);*/
        
        setUploading(prev => ({
            ...prev,
            [type]: true
        }));
        
        /*console.log(`Upload state set to true for ${type}`);*/
        
        try {
            /*console.log(`Creating storage reference for ${type}...`);*/
            const storageRef = ref(storage, `eterna/${patient.id}/${type}_${Date.now()}.stl`);
            /*console.log(`Storage reference created:`, storageRef.fullPath);*/
            
            /*console.log(`Starting upload to Firebase Storage for ${type}...`);*/
            const snapshot = await uploadBytes(storageRef, file);
            /*console.log(`Upload completed for ${type}, getting download URL...`);*/
            
            const downloadURL = await getDownloadURL(snapshot.ref);
            /*console.log(`Download URL obtained for ${type}:`, downloadURL);*/
            
            // Store the URL in the files state
            setFiles(prev => ({
                ...prev,
                [type]: { file, url: downloadURL }
            }));
            
            setUploaded(prev => ({
                ...prev,
                [type]: true
            }));
            
            /*console.log(`Upload process completed successfully for ${type}`);*/
            
        } catch (error) {
            console.error(`Error uploading ${type}:`, error);
            console.error('Error details:', {
                message: error.message,
                code: error.code,
                stack: error.stack
            });
            alert(`Erreur lors de l'upload du fichier ${type}: ${error.message}`);
        } finally {
            /*console.log(`Setting upload state to false for ${type}`);*/
            setUploading(prev => ({
                ...prev,
                [type]: false
            }));
        }
    };

    const handleCommand = async () => {
        try {
            setSaving(true);
            
            // Prepare eterna object with uploaded file URLs
            const eternaData = {};
            
            if (files.maxillaire?.url) {
                eternaData.img1 = files.maxillaire.url;
            }
            
            if (files.mandibule?.url) {
                eternaData.img2 = files.mandibule.url;
            }
            
            // Update patient document
            const updates = {
                eternaCommanded: true,
                eternaCommandDate: serverTimestamp(),
                status: 5,
                traitementEndDate: serverTimestamp()
            };
            
            // Only add eterna object if we have uploaded files
            if (Object.keys(eternaData).length > 0) {
                updates.eterna = eternaData;
            }
            
            await updateDoc(doc(db, "patients", patient.id), updates);
            
            alert("Commande ETERNA effectuée avec succès");
            onBack();
        } catch (error) {
            console.error("Error saving eterna command:", error);
            alert("Erreur lors de la commande ETERNA");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className={styles.eternaContainer}>
            <div className={styles.header}>
                <h3>
                    <span className={styles.backIcon} onClick={onBack}>◀</span>
                    Commande ETERNA®
                </h3>
            </div>

            {patient?.eternaCommanded ? (
                <div className={styles.alreadyCommanded}>
                    <div className={styles.commandedMessage}>
                        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#00b8a9" strokeWidth="3">
                            <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                        <h4>Commande ETERNA déjà effectuée</h4>
                        <p>La commande ETERNA a été effectuée pour ce patient et ne peut plus être modifiée.</p>
                        {patient?.eterna && (
                            <div className={styles.eternaFiles}>
                                <h5>Fichiers uploadés :</h5>
                                {patient.eterna.img1 && <p>✓ Arcade maxillaire</p>}
                                {patient.eterna.img2 && <p>✓ Arcade mandibulaire</p>}
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <>
                    <div className={styles.arcadesContainer}>
                        {/* Maxillaire Section */}
                        <div className={styles.arcadeSection}>
                            <h4>Arcade maxillaire</h4>
                            <div className={styles.arcadeBox}>
                                <div className={styles.arcadeImageContainer}>
                                    <img 
                                        src={maxillaire} 
                                        alt="Arcade maxillaire" 
                                        className={styles.arcadeImage}
                                    />
                                    {uploading.maxillaire && (
                                        <div className={styles.uploadOverlay}>
                                            <div className={styles.loadingIconOverlay}>
                                                <div className={styles.spinner}></div>
                                                <span>Upload en cours...</span>
                                            </div>
                                        </div>
                                    )}
                                    {uploaded.maxillaire && !uploading.maxillaire && (
                                        <div className={styles.uploadOverlay}>
                                            <div className={styles.checkIconOverlay}>
                                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                                                    <polyline points="20 6 9 17 4 12"></polyline>
                                                </svg>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <label 
                                    className={`${styles.uploadBtn} ${uploading.maxillaire ? styles.uploadBtnDisabled : ''}`} 
                                    htmlFor="maxillaire-input"
                                    style={uploading.maxillaire ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
                                >
                                    {uploading.maxillaire ? 'Upload en cours...' : 
                                     uploaded.maxillaire ? 'Fichier uploadé ✓' : 'Select file to upload +'}
                                </label>
                                <input
                                    id="maxillaire-input"
                                    type="file"
                                    accept=".stl,.ply,.obj"
                                    onChange={(e) => handleFileSelect(e, 'maxillaire')}
                                    className={styles.hiddenInput}
                                    disabled={uploading.maxillaire}
                                />
                            </div>
                        </div>

                        {/* Mandibulaire Section */}
                        <div className={styles.arcadeSection}>
                            <h4>Arcade Mandibulaire</h4>
                            <div className={styles.arcadeBox}>
                                <div className={styles.arcadeImageContainer}>
                                    <img 
                                        src={mandibule} 
                                        alt="Arcade mandibulaire" 
                                        className={styles.arcadeImage}
                                    />
                                    {uploading.mandibule && (
                                        <div className={styles.uploadOverlay}>
                                            <div className={styles.loadingIconOverlay}>
                                                <div className={styles.spinner}></div>
                                                <span>Upload en cours...</span>
                                            </div>
                                        </div>
                                    )}
                                    {uploaded.mandibule && !uploading.mandibule && (
                                        <div className={styles.uploadOverlay}>
                                            <div className={styles.checkIconOverlay}>
                                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                                                    <polyline points="20 6 9 17 4 12"></polyline>
                                                </svg>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <label 
                                    className={`${styles.uploadBtn} ${uploading.mandibule ? styles.uploadBtnDisabled : ''}`} 
                                    htmlFor="mandibule-input"
                                    style={uploading.mandibule ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
                                >
                                    {uploading.mandibule ? 'Upload en cours...' : 
                                     uploaded.mandibule ? 'Fichier uploadé ✓' : 'Select file to upload +'}
                                </label>
                                <input
                                    id="mandibule-input"
                                    type="file"
                                    accept=".stl,.ply,.obj"
                                    onChange={(e) => handleFileSelect(e, 'mandibule')}
                                    className={styles.hiddenInput}
                                    disabled={uploading.mandibule}
                                />
                            </div>
                        </div>
                    </div>

                    <div className={styles.commandSection}>
                        <button 
                            className={styles.commandBtn}
                            onClick={handleCommand}
                            disabled={saving}
                        >
                            {saving ? 'Commande en cours...' : 'Commander'}
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

export default EternaContent;
